import React, { useState, useEffect } from 'react';
import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Link,
  ListItem,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { PALETTES } from './appColors';
import { LEGAL, TERMS_INTRO, TERMS_SECTIONS, TERMS_URL } from './legal/terms';

// One block of a section: a paragraph, a bulleted list or an email address.
function Block({ block, p }) {
  if (block.ul) {
    return (
      <UnorderedList spacing="4px" pl="4px" mt="8px">
        {block.ul.map((item) => (
          <ListItem key={item}>{item}</ListItem>
        ))}
      </UnorderedList>
    );
  }
  if (block.email) {
    return (
      <Link href={`mailto:${block.email}`} color={p.accent} fontWeight="500" display="inline-block" mt="6px">
        {block.email}
      </Link>
    );
  }
  return <Text mt="8px">{block.p}</Text>;
}

// Portalled out of the page's themed wrapper, so it takes the literal palette.
const TermsModal = ({ isOpen, onClose, onAgree, isDark = false }) => {
  const p = isDark ? PALETTES.dark : PALETTES.light;
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsScrolledToBottom(false);
    }
  }, [isOpen]);

  const handleScroll = (e) => {
    const target = e.target;
    const atBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 5; // Add 5px tolerance
    setIsScrolledToBottom(atBottom);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg={p.overlay} />
      <ModalContent bg={p.surface} color={p.text} borderRadius="14px" border="1px solid" borderColor={p.border} mx="16px">
        <ModalHeader textAlign="center" pb="10px">
          <Text fontSize="18px" fontWeight="600">Terms and Conditions</Text>
          <Text fontSize="12px" fontWeight="400" color={p.textMuted} mt="2px">
            Last updated {LEGAL.termsUpdated}
          </Text>
        </ModalHeader>
        <ModalCloseButton color={p.textSub} top="14px" />
        <ModalBody
          maxHeight="min(60vh, 480px)"
          overflowY="auto"
          padding="20px 24px"
          color={p.text2}
          fontSize="14px"
          fontWeight="400"
          lineHeight="1.6"
          borderTop="1px solid"
          borderBottom="1px solid"
          borderColor={p.borderSoft}
          onScroll={handleScroll}
        >
          {TERMS_INTRO.map((para, i) => (
            <Text key={para} mt={i ? "8px" : 0}>{para}</Text>
          ))}
          {TERMS_SECTIONS.map((section, i) => (
            <Box key={section.id} mt="20px">
              <Text fontSize="15px" fontWeight="600" color={p.text}>
                {i + 1}. {section.title}
              </Text>
              {section.blocks.map((block, j) => (
                <Block key={j} block={block} p={p} />
              ))}
            </Box>
          ))}
        </ModalBody>
        <ModalFooter flexDirection="column" gap="8px">
          <Button
            onClick={() => {
              onAgree();
              onClose();
            }}
            isDisabled={!isScrolledToBottom}
            sx={{
              padding: "10px 24px",
              background: p.accent,
              color: p.onAccent,
              borderRadius: "12px",
              fontSize: "14px",
              fontWeight: "500",
              lineHeight: "normal",
              minWidth: "140px",
              transition: "background 0.2s",
              _hover: { background: p.accentHover },
              _disabled: { background: p.border, color: p.textMuted, cursor: "not-allowed", opacity: 1 },
              "&[disabled]:hover": { background: p.border },
            }}
          >
            I agree
          </Button>
          <Text fontSize="12px" color={p.textMuted} textAlign="center">
            {isScrolledToBottom ? "Also at " : "Scroll to the end to agree. Also at "}
            <Link href={TERMS_URL} isExternal color={p.textSub} textDecoration="underline">
              {TERMS_URL.replace("https://", "")}
            </Link>
          </Text>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TermsModal;
